import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import TuneIcon from '@mui/icons-material/Tune';
import { Visibility } from '../Panels/Visibility';

import * as state from '@/components/Analysis/state';

import { AnalysisDrawerLeft, AnalysisDrawerRight } from '@/types';
import { AssemblyTabs } from '@/components/Analysis/Viewer/AssemblyTabs';
import { useFeatureFlag, useHavePermission } from '@/hooks';
import { QUERY_DRAWER_COLOR } from '@/constants';
import { FilterSideBar } from '@/components/Analysis/shared/FilterSideBar/FilterSideBar';
import {
  GetAssembliesByTemplateFieldsFiltersForEquipmentsTabQuery,
  TemplateTypeEnum,
  useAllAssemblyTemplatesForFilterSideBarQuery,
  useGetAssembliesByTemplateFieldsFiltersForEquipmentsTabLazyQuery,
} from '@/__generated__/graphql';
import { SideDrawer } from '@/components/shared/SideDrawer';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { statesLoaded } from '@/state';
import { useMeasurementTool } from '../../modules/measurementTool';
import { closeAll, toggleValue } from '@/utils';
import { useFindViewpointsTool } from '../../modules/findViewpointsTool';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { poiState } from '../../modules/pointOfInterest';
import { Permissions } from '@/types';
import { selectedSavedBlock as selectedSavedBlockAtom } from '../../modules/CustomBlocks/state';

const AitInsightsLazy = lazy(() =>
  import('./AitInsights').then(({ AitInsights }) => ({
    default: AitInsights,
  }))
);

const MeasuringToolLazy = lazy(() =>
  import('./MeasuringTool').then(({ MeasuringTool }) => ({
    default: MeasuringTool,
  }))
);

const FindViewpointsToolLazy = lazy(() =>
  import('./FindViewpointsTool').then(({ FindViewpointsTool }) => ({
    default: FindViewpointsTool,
  }))
);

type DrawersProps = {
  onAssemblyLoading?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const Drawers = ({ onAssemblyLoading }: DrawersProps) => {
  const structureId = useGetStructureId();
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const isAitEngineer = useFeatureFlag(['ait-engineer', 'ait-task-lead']);
  const isAdmin = useFeatureFlag('admin');

  const [previousDrawerLeft, setPreviousDrawerLeft] = useState<AnalysisDrawerLeft>();
  const [filteredAssemblies, setFilteredAssemblies] =
    useState<
      GetAssembliesByTemplateFieldsFiltersForEquipmentsTabQuery['getAssembliesByTemplateFieldsFilters']
    >();

  const [assemblyLoading, setAssemblyLoading] = useState(true);

  const [drawerLeft, setDrawerLeft] = useAtom(state.drawerLeft);
  const [drawerRight, setDrawerRight] = useAtom(state.drawerRight);
  const setDrawerRightPreviousState = useSetAtom(state.drawerRightPreviousState);
  const [applyFilters, setApplyFilters] = useAtom(state.applyFilters);
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const [selectedTemplateFilters, setSelectedTemplateFilters] = useAtom(
    state.selectedTemplateFiltersState
  );
  const [selectedDeckIds, setSelectedDeckIds] = useAtom(state.selectedFilteredDeckIds);
  const [selectedPaintRegionIds, setSelectedPaintRegionIds] = useAtom(
    state.selectedFilteredPaintRegionIds
  );
  const [selectedWorkpackIds, setSelectedWorkpackIds] = useAtom(state.selectedFilteredWorkpackIds);

  const selectedAssemblyName = useAtomValue(state.selectedAssemblyName);
  const selectedSpherical = useAtomValue(state.selectedSpherical);
  const isUncontextualised = useAtomValue(state.isUncontextualised);
  const allStatesLoaded = useAtomValue(statesLoaded);

  const selectedSavedBlock = useAtomValue(selectedSavedBlockAtom);
  const showOnlySelectedPoi = useAtomValue(state.showOnlySelectedPoi);

  const setAssemblyTemplate = useSetAtom(state.assemblyTemplate);
  const setAllAssemblyTemplates = useSetAtom(state.allAssemblyTemplates);
  const [selectedTemplateId, setSelectedTemplateId] = useAtom(state.selectedAssemblyTemplateId);

  const canReadDefects = useHavePermission(Permissions.READ_DEFECTS);

  const { data: templatesData } = useAllAssemblyTemplatesForFilterSideBarQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    skip: !structureId || !!selectedTemplateId,
  });

  useEffect(() => {
    const assemblyTemplates = templatesData?.allTemplates;
    if (assemblyTemplates) {
      setAllAssemblyTemplates(templatesData.allTemplates);
      let selectedTemplate = assemblyTemplates[0];
      if (!selectedTemplate) return;
      if (!canReadDefects) {
        selectedTemplate = {
          ...selectedTemplate,
          fields:
            selectedTemplate?.fields?.filter(
              (templateField) =>
                !templateField.permission || templateField.permission !== Permissions.READ_DEFECTS
            ) || [],
        };
      }
      setSelectedTemplateId(selectedTemplate.id);
      setAssemblyTemplate(selectedTemplate);
    }
  }, [
    templatesData?.allTemplates,
    setAssemblyTemplate,
    setSelectedTemplateId,
    setAllAssemblyTemplates,
    canReadDefects,
  ]);

  useEffect(() => {
    setApplyFilters(true);
  }, [setApplyFilters]);

  const { leftDrawerWidth } = useDrawerWidths();
  const handleLeftToggle = useCallback(
    (property: keyof AnalysisDrawerLeft) => {
      setDrawerLeft(toggleValue<AnalysisDrawerLeft>(drawerLeft, property));
    },
    [drawerLeft, setDrawerLeft]
  );

  const handleRightToggle = useCallback(
    (property: keyof AnalysisDrawerRight) => {
      setDrawerRight(toggleValue<AnalysisDrawerRight>(drawerRight, property));
      setDrawerRightPreviousState(property);
    },
    [drawerRight, setDrawerRight, setDrawerRightPreviousState]
  );

  const leftDrawersClosed = useMemo(() => {
    return !Object.values(drawerLeft).includes(true);
  }, [drawerLeft]);

  const rightDrawersClosed = useMemo(() => {
    return !Object.values(drawerRight).includes(true);
  }, [drawerRight]);

  useEffect(() => {
    if (isUncontextualised !== undefined) {
      setDrawerLeft({
        visibility: isUncontextualised,
        query: !isUncontextualised,
      });
    }
  }, [isUncontextualised, setDrawerLeft]);

  useEffect(() => {
    if (showOnlySelectedPoi) {
      return;
    }
    if (selectedAssemblyId) {
      setDrawerLeft((previousDrawerLeft_) => {
        setPreviousDrawerLeft(previousDrawerLeft_);
        return closeAll(previousDrawerLeft_);
      });
    }
  }, [selectedAssemblyId, setDrawerLeft, showOnlySelectedPoi]);

  useEffect(() => {
    if (!selectedAssemblyId && previousDrawerLeft) {
      setDrawerLeft(previousDrawerLeft);
    }
  }, [selectedAssemblyId, previousDrawerLeft, setDrawerLeft]);
  const [fetchFilteredAssemblies] =
    useGetAssembliesByTemplateFieldsFiltersForEquipmentsTabLazyQuery();
  const defaultFiltersForAssemblies = useMemo(() => {
    return [selectedTemplateFilters, selectedPaintRegionIds, selectedDeckIds].every(
      (filters) => filters.length === 0
    );
  }, [selectedDeckIds, selectedTemplateFilters, selectedPaintRegionIds]);

  useEffect(() => {
    if (selectedTemplateId && allStatesLoaded && applyFilters) {
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters,
            structureId,
            templateId: selectedTemplateId ?? '',
            selectedDeckIds,
            selectedPaintRegionIds: [
              ...selectedPaintRegionIds,
              ...(selectedSavedBlock?.paintRegion?.id ? [selectedSavedBlock.paintRegion.id] : []),
            ],
            selectedWorkpackIds,
          },
        },
        fetchPolicy: 'no-cache',
      })
        .then(({ data }) => {
          setFilteredAssemblies(data?.getAssembliesByTemplateFieldsFilters);
          setApplyFilters(false);
        })
        .finally(() => setAssemblyLoading(false));
    }
  }, [
    defaultFiltersForAssemblies,
    selectedTemplateId,
    fetchFilteredAssemblies,
    selectedTemplateFilters,
    structureId,
    setAssemblyLoading,
    selectedDeckIds,
    selectedPaintRegionIds,
    allStatesLoaded,
    applyFilters,
    setApplyFilters,
    selectedWorkpackIds,
    selectedSavedBlock?.paintRegion?.id,
  ]);

  useEffect(() => {
    if (filteredAssemblies && selectedAssemblyName && !selectedAssemblyId) {
      setSelectedAssemblyId(
        filteredAssemblies.find((assembly) => assembly?.tagName === selectedAssemblyName)?.id ??
          undefined
      );
    }
  }, [filteredAssemblies, selectedAssemblyId, selectedAssemblyName, setSelectedAssemblyId]);

  const clearQuery = useCallback(() => {
    setAssemblyLoading(true);
    setFilteredAssemblies(undefined);
  }, [setFilteredAssemblies]);

  useEffect(() => {
    if (onAssemblyLoading) {
      onAssemblyLoading(assemblyLoading);
    }
  }, [assemblyLoading, onAssemblyLoading]);

  const { isMeasurementMenuVisible } = useMeasurementTool();

  const { isFindViewpointsToolActive } = useFindViewpointsTool();

  useEffect(() => {
    if (
      selectedAssemblyId &&
      !filteredAssemblies?.some((assembly) => assembly.id === selectedAssemblyId)
    ) {
      setSelectedTemplateFilters([]);
      setSelectedDeckIds([]);
      setSelectedPaintRegionIds([]);
      setSelectedWorkpackIds([]);
      setApplyFilters(true);
    }
  }, [
    filteredAssemblies,
    selectedAssemblyId,
    setApplyFilters,
    setSelectedDeckIds,
    setSelectedTemplateFilters,
    setSelectedPaintRegionIds,
    setSelectedWorkpackIds,
  ]);
  return (
    <div data-testid="drawers">
      {editPointOfInterest.state === 'Inactive' && (
        <AssemblyTabs
          filteredAssemblies={filteredAssemblies}
          assemblyLoading={assemblyLoading}
          allClosed={rightDrawersClosed}
          isOpen={drawerRight.equipment}
          handleOpen={() => handleRightToggle('equipment')}
        />
      )}
      {(isAitEngineer || isAdmin) && editPointOfInterest.state === 'Inactive' && (
        <Suspense>
          <AitInsightsLazy
            allClosed={rightDrawersClosed}
            isOpen={drawerRight.aitInsight}
            handleOpen={() => handleRightToggle('aitInsight')}
          />
        </Suspense>
      )}

      {isMeasurementMenuVisible && (
        <Suspense>
          <MeasuringToolLazy
            allClosed={rightDrawersClosed}
            isOpen={drawerRight.measuringTool}
            handleOpen={() => handleRightToggle('measuringTool')}
          />
        </Suspense>
      )}

      {isFindViewpointsToolActive && (
        <Suspense>
          <FindViewpointsToolLazy
            allClosed={rightDrawersClosed}
            isOpen={drawerRight.findViewpointsTool}
            handleOpen={() => handleRightToggle('findViewpointsTool')}
          />
        </Suspense>
      )}
      {editPointOfInterest.state === 'Inactive' && (
        <Visibility
          allClosed={leftDrawersClosed}
          isOpen={drawerLeft.visibility}
          handleOpen={() => handleLeftToggle('visibility')}
          data-testid="visibility-drawer"
        />
      )}

      {!selectedSpherical && !selectedAssemblyId && editPointOfInterest.state === 'Inactive' && (
        <SideDrawer
          allDrawersClosed={leftDrawersClosed}
          isOpen={drawerLeft.query}
          handleOpen={() => handleLeftToggle('query')}
          icon={isUncontextualised ? undefined : <TuneIcon />}
          position="left"
          drawerColor={QUERY_DRAWER_COLOR}
          width={leftDrawerWidth}
          label="Query"
          iconStyles={{ top: 65, zIndex: '102 !important' }}
          data-testid="filter-drawer"
        >
          <FilterSideBar onClear={clearQuery} />
        </SideDrawer>
      )}
    </div>
  );
};
